import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Sparkles, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLegalForm } from '../legal-form/useLegalForm';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { mergeComparisonItems } from '../../utils/reportUtils';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ProfitLossReport = ({
  reportData,
  comparisonData,
  showComparison,
  viewMode,
  formatAmount,
  renderVariance,
  showGlCodes,
  openDrilldown,
  aiCommentary,
  loadingAi,
  getCurrencySymbol,
  cogsLabel = 'Cost of Sales',  // Dynamic label: "Cost of Goods Sold", "Cost of Services", or "Cost of Sales"
  cogsCoverage = null             // iter1374 - closing-stock coverage for the period
}) => {
  const { data: legalForm } = useLegalForm();
  const netLabel = legalForm?.report_labels?.net_result || 'Net Profit';
  // iter 732 - when the comparison source is a budget overlay, the column
  // header should reflect the budget's name + scenario instead of generic
  // "Previous".  Falls back to "Previous" for period-vs-period comparisons.
  const isBudgetCompare = comparisonData?.source === 'budget_overlay';
  const compareLabel = isBudgetCompare && comparisonData?.budget_meta?.name
    ? `Budget: ${comparisonData.budget_meta.name}${comparisonData.budget_meta.scenario ? ` (${comparisonData.budget_meta.scenario})` : ''}`
    : 'Previous';
  const [expandedSections, setExpandedSections] = React.useState({
    revenue: true,
    discount: true,
    cogs: true,
    operating: true,
    other: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Helper to calculate variance amount and percentage
  const calculateVariance = (current, previous, inverted = false) => {
    const currentVal = Number(current) || 0;
    const previousVal = Number(previous) || 0;
    const diff = currentVal - previousVal;
    const percentage = previousVal !== 0 ? ((diff / Math.abs(previousVal)) * 100) : (currentVal !== 0 ? 100 : 0);
    const isPositive = inverted ? diff < 0 : diff > 0;
    return { diff, percentage, isPositive };
  };

  // Helper to find comparison item by code or name
  const findComparisonItem = (item, comparisonArray) => {
    if (!comparisonArray || !Array.isArray(comparisonArray)) return null;
    return comparisonArray.find(c => 
      (c.code && item.code && c.code === item.code) || 
      (c.name && item.name && c.name === item.name)
    );
  };

  if (!reportData) return null;

  // Summary View
  if (viewMode === 'summary') {
    const hasDiscounts = (reportData.summary?.discount_allowed || 0) > 0;
    
    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className={`grid grid-cols-1 ${hasDiscounts ? 'md:grid-cols-6' : 'md:grid-cols-5'} gap-4`}>
          {/* Gross Revenue (only show if we have discounts) */}
          {hasDiscounts && (
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Gross Revenue</p>
                <div className="flex items-center">
                  <p className="text-base font-bold text-emerald-600 break-all">{formatAmount(reportData.summary?.gross_revenue)}</p>
                  {showComparison && comparisonData && renderVariance(reportData.summary?.gross_revenue, comparisonData.summary?.gross_revenue)}
                </div>
              </CardContent>
            </Card>
          )}
          {/* Net Revenue (always shown as Total Revenue) */}
          <Card className="bg-[#108981]/5 border-[#108981]/20">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">{hasDiscounts ? 'Net Revenue' : 'Total Revenue'}</p>
              <div className="flex items-center">
                <p className="text-base font-bold text-[#108981] break-all">{formatAmount(reportData.summary?.total_revenue)}</p>
                {showComparison && comparisonData && renderVariance(reportData.summary?.total_revenue, comparisonData.summary?.total_revenue)}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">{cogsLabel}</p>
              <div className="flex items-center">
                <p className="text-base font-bold text-orange-600 break-all">{formatAmount(reportData.summary?.total_cost_of_sales)}</p>
                {showComparison && comparisonData && renderVariance(reportData.summary?.total_cost_of_sales, comparisonData.summary?.total_cost_of_sales, true)}
              </div>
            </CardContent>
          </Card>
          <Card className={`${(reportData.summary?.gross_profit || 0) >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Gross Profit</p>
              <div className="flex items-center">
                <p className={`text-base font-bold break-all ${(reportData.summary?.gross_profit || 0) >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                  {formatAmount(reportData.summary?.gross_profit)}
                </p>
                {showComparison && comparisonData && renderVariance(reportData.summary?.gross_profit, comparisonData.summary?.gross_profit)}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#283F8B]/5 border-[#283F8B]/20">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Operating Expenses</p>
              <div className="flex items-center">
                <p className="text-base font-bold text-[#283F8B] break-all">{formatAmount((reportData.summary?.total_operating_expenses || 0) + (reportData.summary?.total_other_expenses || 0))}</p>
                {showComparison && comparisonData && renderVariance((reportData.summary?.total_operating_expenses || 0) + (reportData.summary?.total_other_expenses || 0), (comparisonData.summary?.total_operating_expenses || 0) + (comparisonData.summary?.total_other_expenses || 0), true)}
              </div>
            </CardContent>
          </Card>
          {(reportData.summary?.total_other_income || 0) !== 0 && (
            <Card className="bg-teal-50 border-teal-200" data-testid="pl-summary-other-income">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Other Income</p>
                <div className="flex items-center">
                  <p className="text-base font-bold text-teal-700 break-all">{formatAmount(reportData.summary?.total_other_income)}</p>
                  {showComparison && comparisonData && renderVariance(reportData.summary?.total_other_income, comparisonData.summary?.total_other_income)}
                </div>
              </CardContent>
            </Card>
          )}
          {(reportData.summary?.total_income_tax || 0) !== 0 && (
            <Card className="bg-rose-50 border-rose-200" data-testid="pl-summary-income-tax">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Income Tax</p>
                <div className="flex items-center">
                  <p className="text-base font-bold text-rose-700 break-all">{formatAmount(reportData.summary?.total_income_tax)}</p>
                  {showComparison && comparisonData && renderVariance(reportData.summary?.total_income_tax, comparisonData.summary?.total_income_tax, true)}
                </div>
              </CardContent>
            </Card>
          )}
          <Card className={`${(reportData.summary?.net_profit || 0) >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground" data-testid="pl-net-result-kpi-label">{netLabel}</p>
              <div className="flex items-center">
                <p className={`text-base font-bold break-all ${(reportData.summary?.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {formatAmount(reportData.summary?.net_profit)}
                </p>
                {showComparison && comparisonData && renderVariance(reportData.summary?.net_profit, comparisonData.summary?.net_profit)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Comparison Table for Summary View */}
        {showComparison && comparisonData && (
          <Card data-testid="pnl-comparison-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Period Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Current Period</TableHead>
                    <TableHead className="text-right">Comparison Period</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-right">Variance %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { label: 'Total Revenue', current: reportData.summary?.total_revenue, previous: comparisonData.summary?.total_revenue, inverted: false },
                    { label: cogsLabel, current: reportData.summary?.total_cost_of_sales, previous: comparisonData.summary?.total_cost_of_sales, inverted: true },
                    { label: 'Gross Profit', current: reportData.summary?.gross_profit, previous: comparisonData.summary?.gross_profit, inverted: false },
                    { label: 'Operating Expenses', current: reportData.summary?.total_operating_expenses, previous: comparisonData.summary?.total_operating_expenses, inverted: true },
                    { label: 'Other Income', current: reportData.summary?.total_other_income, previous: comparisonData.summary?.total_other_income, inverted: false },
                    { label: 'Other Expenses', current: reportData.summary?.total_other_expenses, previous: comparisonData.summary?.total_other_expenses, inverted: true },
                    { label: 'Income Tax', current: reportData.summary?.total_income_tax, previous: comparisonData.summary?.total_income_tax, inverted: true },
                    { label: 'Net Profit', current: reportData.summary?.net_profit, previous: comparisonData.summary?.net_profit, inverted: false, isTotal: true },
                  ].map((row, idx) => {
                    const variance = calculateVariance(row.current, row.previous, row.inverted);
                    return (
                      <TableRow key={idx} className={row.isTotal ? 'font-bold bg-muted/30' : ''}>
                        <TableCell className={row.isTotal ? 'font-semibold' : ''}>{row.label}</TableCell>
                        <TableCell className="text-right font-mono">{formatAmount(row.current)}</TableCell>
                        <TableCell className="text-right font-mono">{formatAmount(row.previous)}</TableCell>
                        <TableCell className={`text-right font-mono ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                          {variance.diff >= 0 ? '+' : ''}{formatAmount(variance.diff)}
                        </TableCell>
                        <TableCell className={`text-right font-mono ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                          <span className="flex items-center justify-end gap-1">
                            {variance.diff !== 0 && (variance.isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />)}
                            {variance.percentage.toFixed(1)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* AI Commentary */}
        {showComparison && (
          <Card className="bg-gradient-to-r from-[#283F8B]/5 to-[#108981]/5 border-[#283F8B]/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#283F8B] to-[#108981] flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-2">AI Analysis</h3>
                  {loadingAi ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyzing your financial data...
                    </div>
                  ) : aiCommentary ? (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground leading-relaxed">{aiCommentary.analysis}</p>
                      {aiCommentary.insights && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {aiCommentary.insights.map((insight, i) => (
                            <Badge key={i} variant={insight.type === 'positive' ? 'default' : insight.type === 'warning' ? 'secondary' : 'destructive'}>
                              {insight.type === 'positive' ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                              {insight.label}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Use “Generate Commentary” in the AI Variance Analysis card below when you want an AI read of this comparison (uses AI credit).</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Detailed View with Comparison Columns
  const renderDetailedSection = (title, items, comparisonItems, color, sectionKey, totalLabel, totalAmount, comparisonTotal, inverted = false) => {
    // Get section-specific AI commentary if available
    const sectionAiComment = aiCommentary?.section_insights?.[sectionKey];
    const totalVariance = calculateVariance(totalAmount, comparisonTotal, inverted);
    
    return (
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-muted/50 transition-colors py-3"
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              {expandedSections[sectionKey] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              {title}
            </CardTitle>
            <span className={`font-bold ${color}`}>{formatAmount(totalAmount)}</span>
          </div>
        </CardHeader>
        <AnimatePresence>
          {expandedSections[sectionKey] && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <CardContent className="pt-0">
                {showComparison && comparisonData ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          {showGlCodes && <TableHead className="w-[80px]">Code</TableHead>}
                          <TableHead>Account</TableHead>
                          <TableHead className="text-right w-[120px]">Current</TableHead>
                          <TableHead className="text-right w-[160px]" data-testid="compare-col-header">{compareLabel}</TableHead>
                          <TableHead className="text-right w-[100px]">Variance</TableHead>
                          <TableHead className="text-right w-[80px]">%</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mergeComparisonItems(items, comparisonItems).map((item, idx) => {
                          const compItem = findComparisonItem(item, comparisonItems);
                          const compAmount = compItem?.balance || compItem?.amount || 0;
                          const variance = calculateVariance(item.balance || item.amount, compAmount, inverted);
                          return (
                            <TableRow 
                              key={idx} 
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => openDrilldown(item)}
                            >
                              {showGlCodes && <TableCell className="text-muted-foreground font-mono text-xs">{item.code}</TableCell>}
                              <TableCell className="text-sm">{item.name}</TableCell>
                              <TableCell className="text-right font-mono text-sm">{formatAmount(item.balance || item.amount)}</TableCell>
                              <TableCell className="text-right font-mono text-sm text-muted-foreground">{formatAmount(compAmount)}</TableCell>
                              <TableCell className={`text-right font-mono text-sm ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                                {variance.diff >= 0 ? '+' : ''}{formatAmount(variance.diff)}
                              </TableCell>
                              <TableCell className={`text-right font-mono text-xs ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                                {variance.percentage.toFixed(1)}%
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Section Total Row */}
                        <TableRow className="bg-muted/30 font-medium">
                          {showGlCodes && <TableCell></TableCell>}
                          <TableCell>{totalLabel}</TableCell>
                          <TableCell className="text-right font-mono">{formatAmount(totalAmount)}</TableCell>
                          <TableCell className="text-right font-mono text-muted-foreground">{formatAmount(comparisonTotal)}</TableCell>
                          <TableCell className={`text-right font-mono ${totalVariance.isPositive ? 'text-green-600' : totalVariance.diff !== 0 ? 'text-red-500' : ''}`}>
                            {totalVariance.diff >= 0 ? '+' : ''}{formatAmount(totalVariance.diff)}
                          </TableCell>
                          <TableCell className={`text-right font-mono text-xs ${totalVariance.isPositive ? 'text-green-600' : totalVariance.diff !== 0 ? 'text-red-500' : ''}`}>
                            {totalVariance.percentage.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    
                    {/* AI Section Commentary */}
                    {sectionAiComment && (
                      <div className="mt-3 p-3 bg-gradient-to-r from-[#5FC6C5]/5 to-transparent border-l-2 border-[#5FC6C5] rounded-r-lg">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-[#5FC6C5] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-muted-foreground leading-relaxed">{sectionAiComment}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-1">
                    {items?.map((item, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between py-2 px-2 hover:bg-muted/50 rounded cursor-pointer transition-colors"
                        onClick={() => openDrilldown(item)}
                      >
                        <span className="text-sm text-foreground">
                          {showGlCodes && item.code && <span className="text-muted-foreground mr-2">{item.code}</span>}
                          {item.name}
                        </span>
                        <span className="font-medium">{formatAmount(item.balance || item.amount)}</span>
                      </div>
                    ))}
                    {(!items || items.length === 0) && (
                      <p className="text-sm text-muted-foreground py-2">No items recorded</p>
                    )}
                  </div>
                )}
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    );
  };

  // Check if we have discount data
  const hasDiscounts = reportData.discount_allowed?.length > 0 || (reportData.summary?.discount_allowed || 0) > 0;

  return (
    <div className="space-y-4" data-testid="pnl-detailed-view">
      {/* Gross Revenue Section (only show if we have discounts) */}
      {hasDiscounts && renderDetailedSection(
        'Gross Revenue',
        reportData.gross_revenue || reportData.revenue,
        comparisonData?.gross_revenue || comparisonData?.revenue,
        'text-emerald-600',
        'revenue',
        'Total Gross Revenue',
        reportData.summary?.gross_revenue || reportData.summary?.total_revenue,
        comparisonData?.summary?.gross_revenue || comparisonData?.summary?.total_revenue,
        false
      )}

      {/* Discount Allowed Section (contra-revenue - Sales Discounts, Returns) */}
      {hasDiscounts && reportData.discount_allowed?.length > 0 && renderDetailedSection(
        'Less: Discount Allowed',
        reportData.discount_allowed,
        comparisonData?.discount_allowed,
        'text-rose-600',
        'discount',
        'Total Discount Allowed',
        reportData.summary?.discount_allowed,
        comparisonData?.summary?.discount_allowed,
        true  // Inverted because discounts reduce revenue
      )}

      {/* Net Revenue Summary (only show if we have discounts) */}
      {hasDiscounts && (
        <Card className="bg-[#108981]/5 border-[#108981]/30">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-[#108981]">Net Revenue</span>
              <div className="flex items-center gap-4">
                <span className="font-bold text-[#108981]">{formatAmount(reportData.summary?.total_revenue)}</span>
                {showComparison && comparisonData && (
                  <>
                    <span className="text-muted-foreground text-sm">vs</span>
                    <span className="text-muted-foreground">{formatAmount(comparisonData.summary?.total_revenue)}</span>
                    {(() => {
                      const v = calculateVariance(reportData.summary?.total_revenue, comparisonData.summary?.total_revenue, false);
                      return (
                        <span className={`font-medium ${v.isPositive ? 'text-green-600' : v.diff !== 0 ? 'text-red-500' : ''}`}>
                          ({v.diff >= 0 ? '+' : ''}{v.percentage.toFixed(1)}%)
                        </span>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue Section (only show if NO discounts - simple view) */}
      {!hasDiscounts && renderDetailedSection(
        'Revenue',
        reportData.revenue,
        comparisonData?.revenue,
        'text-[#108981]',
        'revenue',
        'Total Revenue',
        reportData.summary?.total_revenue,
        comparisonData?.summary?.total_revenue,
        false
      )}

      {/* Cost of Sales Section */}
      {renderDetailedSection(
        cogsLabel,
        reportData.cost_of_sales,
        comparisonData?.cost_of_sales,
        'text-orange-600',
        'cogs',
        `Total ${cogsLabel}`,
        reportData.summary?.total_cost_of_sales,
        comparisonData?.summary?.total_cost_of_sales,
        true
      )}

      {/* iter1374 - Closing-stock coverage hint. Only appears when the org
          tracks inventory and at least one month in the reporting range has
          no closing-stock entry (periodic true-up not yet applied). */}
      {cogsCoverage?.tracks_inventory && !cogsCoverage?.all_covered && (cogsCoverage?.missing_months?.length > 0) && (
        <Card className="bg-amber-50/70 border-amber-200" data-testid="cogs-closing-stock-hint">
          <CardContent className="py-3 px-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900 leading-relaxed">
                <p className="font-medium">
                  Why might {cogsLabel} look off?
                </p>
                <p className="mt-1 text-amber-800">
                  You haven't posted closing stock for
                  {' '}
                  <span className="font-semibold">
                    {cogsCoverage.missing_months.length === 1
                      ? `${MONTH_NAMES[cogsCoverage.missing_months[0].period_month - 1]} ${cogsCoverage.missing_months[0].period_year}`
                      : `${cogsCoverage.missing_months.length} month${cogsCoverage.missing_months.length > 1 ? 's' : ''} in this period`}
                  </span>
                  {' '}yet. Until you do, {cogsLabel} here only reflects per-item costs booked at each sale &mdash; it doesn't include any shrinkage, unsold stock, or opening/closing swings from the periodic count.{' '}
                  <Link
                    to="/inventory?tab=month-end"
                    className="underline font-semibold hover:text-amber-950"
                    data-testid="cogs-hint-enter-closing-stock-link"
                  >
                    Enter closing stock &rarr;
                  </Link>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gross Profit */}
      <Card className="bg-blue-50/50 border-blue-200">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-blue-700">Gross Profit</span>
            <div className="flex items-center gap-4">
              <span className="font-bold text-blue-700">{formatAmount(reportData.summary?.gross_profit)}</span>
              {showComparison && comparisonData && (
                <>
                  <span className="text-muted-foreground text-sm">vs</span>
                  <span className="text-muted-foreground">{formatAmount(comparisonData.summary?.gross_profit)}</span>
                  {(() => {
                    const v = calculateVariance(reportData.summary?.gross_profit, comparisonData.summary?.gross_profit, false);
                    return (
                      <span className={`font-medium ${v.isPositive ? 'text-green-600' : v.diff !== 0 ? 'text-red-500' : ''}`}>
                        ({v.diff >= 0 ? '+' : ''}{v.percentage.toFixed(1)}%)
                      </span>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Operating Expenses Section */}
      {renderDetailedSection(
        'Operating Expenses',
        reportData.operating_expenses,
        comparisonData?.operating_expenses,
        'text-[#283F8B]',
        'operating',
        'Total Operating Expenses',
        reportData.summary?.total_operating_expenses,
        comparisonData?.summary?.total_operating_expenses,
        true
      )}

      {/* Other Income Section — interest, dividends, FX gains: added back after operating profit */}
      {reportData.other_income?.length > 0 && renderDetailedSection(
        'Other Income',
        reportData.other_income,
        comparisonData?.other_income,
        'text-teal-700',
        'other-income',
        'Total Other Income',
        reportData.summary?.total_other_income,
        comparisonData?.summary?.total_other_income,
        false
      )}

      {/* Other Expenses Section */}
      {reportData.other_expenses?.length > 0 && renderDetailedSection(
        'Other Expenses',
        reportData.other_expenses,
        comparisonData?.other_expenses,
        'text-gray-600',
        'other',
        'Total Other Expenses',
        reportData.summary?.total_other_expenses,
        comparisonData?.summary?.total_other_expenses,
        true
      )}

      {/* Income Tax Section — only when the business books tax expense */}
      {reportData.income_tax?.length > 0 && (
        <>
          <Card className="bg-slate-50/60 border-slate-200" data-testid="pl-profit-before-tax">
            <CardContent className="py-3 px-4 flex items-center justify-between">
              <span className="font-semibold text-slate-700">Profit Before Tax</span>
              <span className={`font-bold ${(reportData.summary?.profit_before_tax || 0) >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
                {formatAmount(reportData.summary?.profit_before_tax)}
              </span>
            </CardContent>
          </Card>
          {renderDetailedSection(
            'Income Tax Expense',
            reportData.income_tax,
            comparisonData?.income_tax,
            'text-rose-700',
            'income-tax',
            'Total Income Tax',
            reportData.summary?.total_income_tax,
            comparisonData?.summary?.total_income_tax,
            true
          )}
        </>
      )}

      {/* Net Profit */}
      <Card className={`${(reportData.summary?.net_profit || 0) >= 0 ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'}`}>
        <CardContent className="py-4 px-4">
          <div className="flex items-center justify-between">
            <span className={`font-bold text-lg ${(reportData.summary?.net_profit || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`} data-testid="pl-net-result-label">
              {netLabel}
            </span>
            <div className="flex items-center gap-4">
              <span className={`font-bold text-xl ${(reportData.summary?.net_profit || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                {formatAmount(reportData.summary?.net_profit)}
              </span>
              {showComparison && comparisonData && (
                <>
                  <span className="text-muted-foreground text-sm">vs</span>
                  <span className="text-muted-foreground">{formatAmount(comparisonData.summary?.net_profit)}</span>
                  {(() => {
                    const v = calculateVariance(reportData.summary?.net_profit, comparisonData.summary?.net_profit, false);
                    return (
                      <span className={`font-semibold ${v.isPositive ? 'text-green-600' : v.diff !== 0 ? 'text-red-500' : ''}`}>
                        ({v.diff >= 0 ? '+' : ''}{v.percentage.toFixed(1)}%)
                      </span>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Other Comprehensive Income (OCI) Section */}
      {(reportData.other_comprehensive_income?.accounts?.length > 0 || reportData.other_comprehensive_income?.total !== 0) && (
        <>
          <div className="mt-6 mb-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Other Comprehensive Income
            </h3>
          </div>
          
          {reportData.other_comprehensive_income?.accounts?.map((account, idx) => (
            <div key={idx} className="flex items-center justify-between py-2 px-4 border-b border-border/50">
              <span className="text-sm text-muted-foreground pl-4">{account.name}</span>
              <div className="flex items-center gap-4">
                <span className={`text-sm ${account.amount >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                  {formatAmount(account.amount)}
                </span>
              </div>
            </div>
          ))}

          {/* Total Comprehensive Income */}
          <Card className={`mt-2 ${(reportData.total_comprehensive_income || 0) >= 0 ? 'bg-blue-50/50 border-blue-200' : 'bg-orange-50/50 border-orange-200'}`}>
            <CardContent className="py-4 px-4">
              <div className="flex items-center justify-between">
                <span className={`font-bold text-lg ${(reportData.total_comprehensive_income || 0) >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>
                  Total Comprehensive Income
                </span>
                <div className="flex items-center gap-4">
                  <span className={`font-bold text-xl ${(reportData.total_comprehensive_income || 0) >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>
                    {formatAmount(reportData.total_comprehensive_income)}
                  </span>
                  {showComparison && comparisonData && comparisonData.total_comprehensive_income !== undefined && (
                    <>
                      <span className="text-muted-foreground text-sm">vs</span>
                      <span className="text-muted-foreground">{formatAmount(comparisonData.total_comprehensive_income)}</span>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ProfitLossReport;
